/**
 * Condition evaluator.
 *
 * P3-011: Evaluates typed condition descriptors against a runtime context.
 * No arbitrary expressions, JSONPath, or eval.
 */
import type {
  ConditionDescriptor,
  EqualsCondition,
  NotEqualsCondition,
  InCondition,
  NotInCondition,
  ExistsCondition,
  AndCondition,
  OrCondition,
  PermissionAvailableCondition,
  ModuleAvailableCondition,
  FieldValueCondition,
} from '@finance/contracts';

export interface EvaluationContext {
  /** Resolved permission codes for the current user. */
  permissions: readonly string[];
  /** Enabled module keys for the current branch. */
  enabledModules: readonly string[];
  /** Runtime data record used by field-based conditions. */
  data?: Record<string, unknown>;
}

function getValueAtPath(record: Record<string, unknown> | undefined, path: string): unknown {
  if (!record) return undefined;
  if (path in record) return record[path];
  const parts = path.split('.');
  let current: unknown = record;
  for (const part of parts) {
    if (current === null || current === undefined) return undefined;
    if (typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

function evaluateEquals(condition: EqualsCondition, ctx: EvaluationContext): boolean {
  const value = getValueAtPath(ctx.data, condition.field);
  return value === condition.value;
}

function evaluateNotEquals(condition: NotEqualsCondition, ctx: EvaluationContext): boolean {
  const value = getValueAtPath(ctx.data, condition.field);
  return value !== condition.value;
}

function evaluateIn(condition: InCondition, ctx: EvaluationContext): boolean {
  const value = getValueAtPath(ctx.data, condition.field);
  return condition.values.some((v) => v === value);
}

function evaluateNotIn(condition: NotInCondition, ctx: EvaluationContext): boolean {
  const value = getValueAtPath(ctx.data, condition.field);
  return condition.values.every((v) => v !== value);
}

function evaluateExists(condition: ExistsCondition, ctx: EvaluationContext): boolean {
  const value = getValueAtPath(ctx.data, condition.field);
  return value !== undefined && value !== null;
}

function evaluateAnd(condition: AndCondition, ctx: EvaluationContext): boolean {
  return condition.conditions.every((c) => evaluateCondition(c, ctx));
}

function evaluateOr(condition: OrCondition, ctx: EvaluationContext): boolean {
  return condition.conditions.some((c) => evaluateCondition(c, ctx));
}

function evaluatePermissionAvailable(condition: PermissionAvailableCondition, ctx: EvaluationContext): boolean {
  // P0-4: Permissions are no longer exposed to the client. The server authorizes
  // every operation via access.evaluate() before returning actions/descriptors.
  // When permissions are unavailable (server-authoritative mode), trust the
  // server's decision and treat the condition as satisfied. Returning false here
  // caused a regression where actions with permissionAvailable visibility
  // conditions were always hidden.
  if (ctx.permissions.length === 0) return true;
  return ctx.permissions.includes(condition.permission);
}

function evaluateModuleAvailable(condition: ModuleAvailableCondition, ctx: EvaluationContext): boolean {
  return ctx.enabledModules.includes(condition.module);
}

function evaluateFieldValue(condition: FieldValueCondition, ctx: EvaluationContext): boolean {
  const value = getValueAtPath(ctx.data, condition.field);
  const target = getValueAtPath(value as Record<string, unknown> | undefined, condition.path);
  return target === condition.value;
}

export function evaluateCondition(condition: ConditionDescriptor, ctx: EvaluationContext): boolean {
  switch (condition.operator) {
    case 'equals':
      return evaluateEquals(condition as EqualsCondition, ctx);
    case 'notEquals':
      return evaluateNotEquals(condition as NotEqualsCondition, ctx);
    case 'in':
      return evaluateIn(condition as InCondition, ctx);
    case 'notIn':
      return evaluateNotIn(condition as NotInCondition, ctx);
    case 'exists':
      return evaluateExists(condition as ExistsCondition, ctx);
    case 'and':
      return evaluateAnd(condition as AndCondition, ctx);
    case 'or':
      return evaluateOr(condition as OrCondition, ctx);
    case 'permissionAvailable':
      return evaluatePermissionAvailable(condition as PermissionAvailableCondition, ctx);
    case 'moduleAvailable':
      return evaluateModuleAvailable(condition as ModuleAvailableCondition, ctx);
    case 'fieldValue':
      return evaluateFieldValue(condition as FieldValueCondition, ctx);
    default:
      return false;
  }
}
